import { EpubNavigator, EpubPreferences } from "@readium/navigator";
import { HttpFetcher, Layout, Manifest, Publication, ReadingProgression } from "@readium/shared";
import { fromLocatorJson, toLocatorJson } from "./locator.js";
import { toEpubPreferences } from "./preferences.js";
export class ReaderError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = "ReaderError";
    }
}
/** Charge un Readium Web Publication Manifest (servi par `server/`, readium serve). */
/**
 * Langue du document : sans elle, le navigateur ne coupe aucun mot (`hyphens: auto` ne fait rien).
 * Readium 2.11 devrait la reprendre des métadonnées du livre, mais compare les types de média par
 * référence et ne le fait jamais. On applique sa règle : la langue du `body` si seul lui la porte,
 * sinon la première langue du livre, et rien quand le document a déjà la sienne.
 */
export function applyDocumentLanguage(doc, language) {
    const root = doc.documentElement;
    if (!root || root.getAttribute("lang") || root.getAttribute("xml:lang"))
        return;
    const fromBody = doc.body?.getAttribute("lang") || doc.body?.getAttribute("xml:lang");
    const lang = fromBody || language;
    if (!lang)
        return;
    root.setAttribute("lang", lang);
    if (root.namespaceURI === "http://www.w3.org/1999/xhtml")
        root.setAttributeNS("http://www.w3.org/XML/1998/namespace", "xml:lang", lang);
}
export async function openPublication(manifestUrl, fetchImpl = fetch) {
    let response;
    try {
        response = await fetchImpl(manifestUrl, { credentials: "include" });
    }
    catch (e) {
        throw new ReaderError("manifest-unavailable", `Manifest injoignable : ${String(e)}`);
    }
    if (response.status === 403 || response.status === 400) {
        throw new ReaderError("forbidden", "Accès refusé à cette publication.");
    }
    if (response.status === 410) {
        throw new ReaderError("expired", "Le lien de lecture a expiré.");
    }
    if (!response.ok) {
        throw new ReaderError("manifest-unavailable", `Manifest indisponible (HTTP ${response.status}).`);
    }
    const manifest = Manifest.deserialize(await response.json());
    if (!manifest)
        throw new ReaderError("manifest-invalid", "Manifest invalide.");
    manifest.setSelfLink(manifestUrl);
    return new Publication({ manifest, fetcher: new HttpFetcher(undefined, manifestUrl) });
}
const STYLE_ID = "msb-reader-bridge-styles";
/**
 * Le navigateur Readium crée des iframes `.readium-navigator-iframe` positionnées en absolu,
 * sans dimension : l'hôte doit les étirer sur le conteneur. On l'injecte une fois ici pour que
 * le reader Web et la WebView desktop se comportent pareil.
 */
export const navigatorStyles = `
.readium-navigator-iframe {
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}
`;
function ensureNavigatorStyles(container) {
    const doc = container.ownerDocument;
    if (!doc.getElementById(STYLE_ID)) {
        const style = doc.createElement("style");
        style.id = STYLE_ID;
        style.textContent = navigatorStyles;
        doc.head.appendChild(style);
    }
    if (getComputedStyle(container).position === "static")
        container.style.position = "relative";
}
const promisify = (run) => new Promise((resolve) => run(resolve));
/**
 * Neutralise le balayage de Readium (module ColumnSnapper, à l'écoute des événements tactiles de la
 * page) : on arrête les gestes à un doigt avant lui. Les taps passent par les événements de pointeur
 * et restent actifs ; le zoom à deux doigts aussi.
 */
function blockSwipe(wnd, onSwipe) {
    const marker = wnd;
    if (marker.__msbSwipeBlocked)
        return;
    marker.__msbSwipeBlocked = true;
    let start;
    const stop = (e) => {
        if (e.touches.length > 1)
            return;
        e.stopImmediatePropagation();
        const point = e.touches[0] ?? e.changedTouches[0];
        if (e.type === "touchstart" && point)
            start = { x: point.clientX, y: point.clientY };
        if (e.type === "touchend" && start && point && Math.hypot(point.clientX - start.x, point.clientY - start.y) > 10)
            onSwipe();
    };
    for (const type of ["touchstart", "touchmove", "touchend"]) {
        wnd.addEventListener(type, stop, { capture: true, passive: true });
    }
    // Pas de défilement natif horizontal : un doigt qui glisse ne déplace plus la page.
    wnd.document.documentElement.style.touchAction = "pinch-zoom";
}
/*
 * Un surlignage que l'API CSS Highlight ne sait pas dessiner est un bloc posé sur le texte avec
 * « mix-blend-mode: multiply ». Safari sur iPhone ne le mélange pas et le bloc cache le texte : on lui
 * donne une couleur translucide, celle qui donne la même teinte sur du blanc, et un mélange normal.
 * Repris de Studio, qui a rencontré le problème.
 */
const OVERLAY_ALPHA = 0.4;
/** « rgb(252, 250, 193) » → la couleur translucide qui donne la même teinte sur du blanc. */
export const translucentTint = (tint) => {
    const rgb = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/.exec(tint.trim());
    if (!rgb || (rgb[4] !== undefined && Number(rgb[4]) < 1))
        return tint;
    const [r, g, b] = rgb.slice(1, 4).map((value) => Math.max(0, Math.round(255 - (255 - Number(value)) / OVERLAY_ALPHA)));
    return `rgba(${r}, ${g}, ${b}, ${OVERLAY_ALPHA})`;
};
/*
 * Dans les navigateurs WebKit (Safari sur Mac, tous les navigateurs sur iPhone), l'API CSS Highlight
 * peint aussi par-dessus le texte : les surlignages y sont dessinés en blocs translucides. Readium
 * cherche « Highlight » dans la fenêtre de l'app à chaque groupe de décorations : le retirer avant
 * l'ouverture du livre suffit.
 */
export const isWebKitEngine = (userAgent) => /AppleWebKit\//.test(userAgent) && !/(Chrome|Chromium|Edg|OPR|Android)\b/.test(userAgent);
function drawHighlightsAsBlocksInWebKit() {
    if (typeof window === "undefined" || !("Highlight" in window) || !isWebKitEngine(window.navigator.userAgent))
        return;
    try {
        delete window.Highlight;
    }
    catch {
        // Non supprimable : Readium garde l'API CSS Highlight, toujours en couleur translucide.
    }
}
const unblendOverlay = (block) => {
    if (block.dataset.msbUnblended)
        return;
    block.dataset.msbUnblended = "1";
    if (!block.style.backgroundColor)
        return;
    block.style.setProperty("background-color", translucentTint(block.style.backgroundColor), "important");
    block.style.setProperty("mix-blend-mode", "normal", "important");
    block.style.setProperty("transform", "none", "important");
};
const watchedOverlays = new WeakSet();
function unblendOverlays(doc) {
    for (const host of Array.from(doc.body?.children ?? [])) {
        const root = host.shadowRoot;
        if (!root)
            continue;
        root.querySelectorAll(".readium-highlight").forEach(unblendOverlay);
        if (watchedOverlays.has(root))
            continue;
        watchedOverlays.add(root);
        // Redessinés à chaque redimensionnement et à chaque page : corrigés dès qu'ils sont ajoutés.
        new MutationObserver(() => root.querySelectorAll(".readium-highlight").forEach(unblendOverlay)).observe(root, {
            childList: true,
            subtree: true,
        });
    }
}
const collapse = (text) => text.replace(/\s+/g, " ");
const SELECTION_CONTEXT = 40;
/** Les cadres des pages du livre. */
const pageFrames = (container) => [...container.querySelectorAll(".readium-navigator-iframe")];
/**
 * Décrit la sélection d'une page : son Locator (texte avant et après, qui retrouvent cette occurrence-là),
 * au format de Studio et du mobile, et sa place à l'écran (la page peut être affichée réduite).
 */
function describeSelection(publication, frame, href, position) {
    const win = frame.contentWindow;
    const picked = win?.getSelection();
    if (!win || !picked || picked.rangeCount === 0 || picked.isCollapsed)
        return null;
    const range = picked.getRangeAt(0);
    const highlight = collapse(range.toString()).trim();
    if (!highlight)
        return null;
    const body = win.document.body;
    const before = win.document.createRange();
    before.selectNodeContents(body);
    before.setEnd(range.startContainer, range.startOffset);
    const after = win.document.createRange();
    after.selectNodeContents(body);
    after.setStart(range.endContainer, range.endOffset);
    const beforeText = before.toString();
    const total = Math.max(1, (body.textContent ?? "").length);
    const files = publication.readingOrder.items;
    const index = files.findIndex((file) => file.href === href);
    const progression = Math.min(1, beforeText.length / total);
    const frameRect = frame.getBoundingClientRect();
    const scale = frame.clientWidth > 0 ? frameRect.width / frame.clientWidth : 1;
    const box = range.getBoundingClientRect();
    const fixed = publication.metadata.effectiveLayout === Layout.fixed;
    return {
        locator: {
            href,
            type: files[index]?.type ?? "application/xhtml+xml",
            locations: {
                progression,
                ...(fixed ? { position: index + 1 } : position !== undefined ? { position } : {}),
                ...(index >= 0 ? { totalProgression: (index + progression) / files.length } : {}),
            },
            text: {
                before: collapse(beforeText).slice(-SELECTION_CONTEXT).replace(/^\s+/, ""),
                highlight,
                after: collapse(after.toString().slice(0, 200)).slice(0, SELECTION_CONTEXT),
            },
        },
        rect: { left: frameRect.left + box.left * scale, top: frameRect.top + box.top * scale, width: box.width * scale, height: box.height * scale },
    };
}
/** Couleur du passage surligné (occurrence de recherche, citation du chat) : un jaune pâle, comme dans Studio. */
const PASSAGE_TINT = "rgb(252, 250, 193)";
/** Le paragraphe d'une citation, en teinte plus légère : visible même si le texte exact n'est pas retrouvé. */
const PASSAGE_BLOCK_TINT = "rgb(254, 253, 235)";
/** Le Locator sans son cssSelector : son texte est cherché dans tout le fichier. */
function withoutSelector(locator) {
    const { cssSelector: _cssSelector, ...locations } = locator.locations ?? {};
    return { ...locator, locations };
}
const PASSAGE_GROUP = "msb-passage";
const ANNOTATION_GROUP = "msb-annotations";
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
/** Une page du livre est-elle affichée ? Readium masque les cadres pendant ses transitions. */
/**
 * Text in vertical lines (Japanese, Chinese): Readium 2.11 shows a chapter as one strip scrolled sideways, and its
 * « next page » goes straight to the next chapter. Turning a page moves the strip by one screen first; past the end of
 * the strip (or before its start) it is Readium's turn. Returns false when the page is not vertical or is at that edge.
 */
function scrollVerticalText(container, forward) {
    const frame = [...container.querySelectorAll(".readium-navigator-iframe")].find((f) => f.style.visibility !== "hidden");
    const wnd = frame?.contentWindow;
    const page = wnd?.document.scrollingElement;
    if (!wnd || !page)
        return false;
    const mode = wnd.getComputedStyle(wnd.document.documentElement).writingMode;
    if (!mode.startsWith("vertical") && !mode.startsWith("sideways"))
        return false;
    const max = page.scrollWidth - wnd.innerWidth;
    // Lines from right to left (vertical-rl): the strip scrolls to the left, scrollLeft goes from 0 down to -max.
    const sign = mode.endsWith("-lr") ? 1 : -1;
    const done = Math.abs(page.scrollLeft);
    if (max <= 1 || (forward ? done >= max - 1 : done <= 1))
        return false;
    const target = forward ? Math.min(max, done + wnd.innerWidth) : Math.max(0, done - wnd.innerWidth);
    page.scrollTo({ left: sign * target, behavior: "instant" });
    return true;
}
function frameShown(container) {
    return [...container.querySelectorAll(".readium-navigator-iframe")].some((f) => f.style.visibility !== "hidden");
}
export async function createReader(options) {
    const { container, publication, callbacks = {} } = options;
    ensureNavigatorStyles(container);
    drawHighlightsAsBlocksInWebKit();
    let positions = [];
    try {
        positions = await publication.positionsFromManifest();
    }
    catch {
        // Pas de service de positions : la navigation reste possible, sans numéro de page.
    }
    /*
     * Reprise de lecture : Readium 2.11 s'ouvre au début de la ressource, et des messages tardifs du
     * chargement l'y ramènent même après avoir rejoint la page mémorisée. Pendant RESTORE_GUARD ms,
     * ces positions parasites sont ignorées et on retourne à la page lue ; toute navigation du lecteur
     * met fin à la protection.
     */
    const initial = options.initialLocator ? fromLocatorJson(options.initialLocator) : undefined;
    const restoreTarget = initial?.locations?.position;
    let restoring = !!initial && typeof restoreTarget === "number" && (initial.locations?.progression ?? 0) > 0;
    let restorePending = false;
    const scheduleRestore = () => {
        if (restorePending || !initial)
            return;
        restorePending = true;
        setTimeout(() => {
            restorePending = false;
            if (restoring)
                navigator.go(initial, false, () => { });
        }, 60);
    };
    const RESTORE_GUARD = 2000;
    let edgeTapRatio = options.edgeTapRatio ?? 0;
    const rtl = publication.metadata.effectiveReadingProgression === ReadingProgression.rtl;
    // Dernière sélection lue dans les pages, et dernière signalée à l'hôte (voir `poll`).
    let lastPolled = "";
    let emitted = "";
    // Tap ou clic dans la page, en pixels physiques depuis le bord gauche du cadre (format Readium).
    // Fin d'un glissement du doigt : Readium la signale comme un tap, qu'on ignore.
    let swipeEndedAt = 0;
    // Un appui long sélectionne du texte : la fin de ce geste ne tourne pas la page et n'ouvre pas l'interface.
    const selecting = () => pageFrames(container).some((frame) => {
        try {
            const picked = frame.contentWindow?.getSelection();
            return !!picked && !picked.isCollapsed && picked.toString().trim().length > 0;
        }
        catch {
            return false;
        }
    });
    const onPointer = (event) => {
        if (options.disableSwipe && performance.now() - swipeEndedAt < 500)
            return true;
        if (selecting())
            return true;
        if (edgeTapRatio <= 0 || event.interactiveElement)
            return false; // liens : laissés à Readium
        const width = container.clientWidth * (container.ownerDocument.defaultView?.devicePixelRatio ?? 1);
        if (width <= 0)
            return false;
        // Par le contrôleur, pour profiter de la file des navigations.
        if (event.x < width * edgeTapRatio)
            void controller.goLeft();
        else if (event.x > width * (1 - edgeTapRatio))
            void controller.goRight();
        else
            callbacks.onCenterTapped?.();
        return true;
    };
    const listeners = {
        frameLoaded: (wnd) => {
            applyDocumentLanguage(wnd.document, publication.metadata.languages?.[0]);
            if (options.disableSwipe)
                blockSwipe(wnd, () => (swipeEndedAt = performance.now()));
        },
        positionChanged: (locator) => {
            if (restoring) {
                const position = locator.locations?.position;
                if (position !== restoreTarget) {
                    // Position transitoire de l'ouverture : ni affichée ni enregistrée ; on revient à la page lue.
                    scheduleRestore();
                    return;
                }
            }
            callbacks.onPositionChanged?.(toLocatorJson(locator));
        },
        timelineItemChanged: () => { },
        tap: (event) => onPointer(event),
        click: (event) => onPointer(event),
        zoom: () => { },
        miscPointer: () => callbacks.onCenterTapped?.(),
        scroll: () => { },
        customEvent: () => { },
        handleLocator: (locator) => {
            const href = locator.href;
            if (/^(https?|mailto|tel):/.test(href)) {
                callbacks.onExternalLink?.(href);
                return true;
            }
            callbacks.onInternalLink?.();
            return false;
        },
        textSelected: (selection) => {
            callbacks.onTextSelected?.(selection.text);
            if (!callbacks.onSelection)
                return;
            const frames = pageFrames(container);
            const frame = frames.find((f) => f.src === selection.targetFrameSrc) ?? frames.find((f) => f.style.visibility !== "hidden");
            const current = navigator?.currentLocator;
            const info = frame && current ? describeSelection(publication, frame, current.href, current.locations?.position) : null;
            if (!info)
                return;
            // La lecture régulière des pages ne la signalera pas une seconde fois.
            emitted = lastPolled = `${info.locator.text?.highlight}|${Math.round(frame.contentWindow.getSelection().getRangeAt(0).getBoundingClientRect().top)}`;
            callbacks.onSelection(info);
        },
        contentProtection: () => { },
        contextMenu: () => { },
        peripheral: () => { },
    };
    // Mise en page fixe : une page ou deux. Readium lit le `spread` du livre quand il construit ses pages.
    const fixedLayout = publication.metadata.effectiveLayout === Layout.fixed;
    const publisherSpread = publication.metadata.otherMetadata?.spread;
    let appliedSpreads = options.preferences.spreads ?? null;
    const applySpreads = (spreads) => {
        appliedSpreads = spreads;
        const value = spreads === false ? "none" : spreads === true ? "both" : publisherSpread;
        // Un livre sans autres métadonnées n'en a pas : on les crée pour y mettre le réglage.
        const meta = (publication.metadata.otherMetadata ??= {});
        if (value === undefined)
            delete meta.spread;
        else
            meta.spread = value;
    };
    if (fixedLayout)
        applySpreads(appliedSpreads);
    let epubPreferences = toEpubPreferences(options.preferences, options.isSystemDark, options.layout);
    // Le passage surligné : redessiné sur chaque navigateur construit (voir `recover`).
    let passage = [];
    // Les surlignages des annotations : redessinés sur chaque navigateur construit (voir `recover`).
    let annotationDecorations = [];
    const annotationObserver = {
        onDecorationActivated: (event) => {
            if (selecting())
                return false;
            const frame = pageFrames(container).find((f) => f.style.visibility !== "hidden");
            const at = frame?.getBoundingClientRect();
            const scale = frame && frame.clientWidth > 0 && at ? at.width / frame.clientWidth : 1;
            // La place du surlignage arrive en pixels physiques, depuis la page elle-même.
            const ratio = container.ownerDocument.defaultView?.devicePixelRatio ?? 1;
            const rect = event.rect;
            callbacks.onAnnotationActivated?.(String(event.decoration.id), at && rect
                ? { left: at.left + (rect.left / ratio) * scale, top: at.top + (rect.top / ratio) * scale, width: (rect.width / ratio) * scale, height: (rect.height / ratio) * scale }
                : undefined);
            return true;
        },
    };
    const drawAnnotations = (nav) => {
        nav.registerDecorationObserver(ANNOTATION_GROUP, annotationObserver);
        if (!annotationDecorations.length)
            return;
        nav.applyDecorations(annotationDecorations, ANNOTATION_GROUP);
        // Une page n'écoute les clics sur un groupe qu'un moment après sa création : on le renvoie.
        for (const delay of [250, 900])
            setTimeout(() => nav.applyDecorations(annotationDecorations, ANNOTATION_GROUP), delay);
    };
    const drawPassage = (nav) => {
        nav.applyDecorations(passage, PASSAGE_GROUP);
        // Les blocs de surlignage apparaissent un moment après, dans les pages.
        for (const delay of [100, 600]) {
            setTimeout(() => {
                for (const frame of container.querySelectorAll(".readium-navigator-iframe")) {
                    try {
                        if (frame.contentDocument)
                            unblendOverlays(frame.contentDocument);
                    }
                    catch {
                        // Page en cours de remplacement.
                    }
                }
            }, delay);
        }
    };
    const build = async (locator) => {
        const nav = new EpubNavigator(container, publication, listeners, positions, locator, {
            preferences: epubPreferences,
            defaults: {},
        });
        await nav.load();
        drawAnnotations(nav);
        if (passage.length)
            drawPassage(nav);
        return nav;
    };
    let navigator;
    try {
        navigator = await build(initial);
    }
    catch (e) {
        throw new ReaderError("render-failed", `Impossible d'afficher la publication : ${String(e)}`);
    }
    if (restoring) {
        if (navigator.currentLocator.locations?.position !== restoreTarget)
            scheduleRestore();
        setTimeout(() => {
            restoring = false;
            callbacks.onPositionChanged?.(toLocatorJson(navigator.currentLocator));
        }, RESTORE_GUARD);
    }
    /*
     * Readium 2.11 refuse une navigation tant que la précédente n'est pas terminée : on les
     * enchaîne, chacune attend que la précédente ait affiché sa page.
     *
     * Il arrive aussi qu'une navigation ne se termine jamais (le cadre de la page visée, jeté puis
     * recréé par la navigation précédente, ne signale pas son chargement) : page blanche et plus
     * aucune navigation possible. Passé NAVIGATION_TIMEOUT, on reconstruit le navigateur sur la
     * position visée, que Readium a déjà enregistrée.
     */
    const NAVIGATION_TIMEOUT = 3000;
    const recover = async () => {
        const target = navigator.currentLocator;
        // La destruction attend parfois le cadre bloqué lui-même : on ne l'attend pas plus d'une seconde.
        await Promise.race([navigator.destroy().catch(() => { }), sleep(1000)]);
        container.replaceChildren();
        navigator = await build(target);
        callbacks.onPositionChanged?.(toLocatorJson(navigator.currentLocator));
    };
    let queue = Promise.resolve();
    let pending = 0;
    const navigate = (run) => {
        restoring = false; // le lecteur navigue : la reprise de lecture est terminée
        if (pending++ === 0)
            callbacks.onBusyChanged?.(true);
        const next = queue.then(async () => {
            const outcome = await Promise.race([promisify(run), sleep(NAVIGATION_TIMEOUT).then(() => "timeout")]);
            if (outcome === "timeout") {
                await recover();
                return true;
            }
            for (let waited = 0; outcome && !frameShown(container) && waited < NAVIGATION_TIMEOUT; waited += 50)
                await sleep(50);
            return outcome;
        });
        queue = next.catch(() => false);
        void queue.then(() => {
            if (--pending === 0)
                callbacks.onBusyChanged?.(false);
        });
        return next;
    };
    // Changer de fichier HTML prend parfois une seconde : un second tap ne doit pas sauter une page.
    const turn = (run) => (pending > 0 ? Promise.resolve(false) : navigate(run));
    /*
     * Sur écran tactile, le navigateur sélectionne un mot par un appui long, et les événements de pointeur
     * qu'écoute Readium ne se terminent pas toujours : la sélection est aussi lue dans les pages, une fois
     * qu'elle ne bouge plus (comme dans Studio).
     */
    const noMenu = new WeakSet();
    const poll = setInterval(() => {
        if (!callbacks.onSelection)
            return;
        for (const frame of pageFrames(container)) {
            const win = frame.contentWindow;
            if (!win || frame.style.visibility === "hidden")
                continue;
            try {
                if (!noMenu.has(win)) {
                    noMenu.add(win);
                    // Le menu système de l'appui long (copier, rechercher…) couvrirait le nôtre ; sauf sur Firefox,
                    // dont l'appui long ne sélectionne le mot que si cet événement passe.
                    if (!/Firefox\//.test(win.navigator.userAgent))
                        win.document.addEventListener("contextmenu", (event) => event.preventDefault());
                }
                const picked = win.getSelection();
                const text = picked && !picked.isCollapsed ? collapse(picked.toString()).trim() : "";
                const key = text ? `${text}|${Math.round(picked.getRangeAt(0).getBoundingClientRect().top)}` : "";
                if (key === lastPolled && key !== emitted) {
                    emitted = key;
                    const current = navigator.currentLocator;
                    callbacks.onSelection(key ? describeSelection(publication, frame, current.href, current.locations?.position) : null);
                }
                lastPolled = key;
                if (key)
                    break;
            }
            catch {
                // Page en cours de remplacement : au prochain tour.
            }
        }
    }, 300);
    const controller = {
        publication,
        positionCount: positions.length,
        get currentLocator() {
            return toLocatorJson(navigator.currentLocator);
        },
        goToPosition: (position) => {
            const target = positions[Math.min(positions.length, Math.max(1, Math.round(position))) - 1];
            return target ? navigate((cb) => navigator.go(target, true, cb)) : Promise.resolve(false);
        },
        setEdgeTapRatio: (ratio) => {
            edgeTapRatio = Math.min(0.5, Math.max(0, ratio));
        },
        goForward: () => (pending === 0 && scrollVerticalText(container, true) ? Promise.resolve(true) : turn((cb) => navigator.goForward(true, cb))),
        goBackward: () => (pending === 0 && scrollVerticalText(container, false) ? Promise.resolve(true) : turn((cb) => navigator.goBackward(true, cb))),
        rtl,
        goLeft: () => (rtl ? controller.goForward() : controller.goBackward()),
        goRight: () => (rtl ? controller.goBackward() : controller.goForward()),
        goTo: (locator) => {
            const parsed = fromLocatorJson(locator);
            if (!parsed)
                return Promise.resolve(false);
            // Readium ne cherche le texte cité que dans l'élément du cssSelector. Celui du chatbot est le premier
            // paragraphe du passage, et la citation peut être dans un suivant : d'abord le paragraphe, puis le
            // texte exact dans tout le fichier (comme Studio).
            if (locator.text?.highlight && locator.locations?.cssSelector) {
                const paragraph = fromLocatorJson({ ...locator, text: undefined });
                const text = fromLocatorJson(withoutSelector(locator));
                if (paragraph && text) {
                    return navigate((cb) => navigator.go(paragraph, true, cb)).then((ok) => (ok ? navigate((cb) => navigator.go(text, true, cb)) : false));
                }
            }
            return navigate((cb) => navigator.go(parsed, true, cb));
        },
        goToHref: (href) => {
            const link = publication.linkWithHref(href);
            return link ? navigate((cb) => navigator.goLink(link, true, cb)) : Promise.resolve(false);
        },
        highlight: (locator) => {
            passage = [];
            // Texte exact : cherché dans tout le fichier, pas seulement dans le paragraphe du cssSelector.
            const exact = locator ? fromLocatorJson(locator.text?.highlight ? withoutSelector(locator) : locator) : undefined;
            if (locator && exact) {
                const block = locator.locations?.cssSelector && locator.text?.highlight ? fromLocatorJson({ ...locator, text: undefined }) : undefined;
                if (block)
                    passage.push({ id: "passage-block", locator: block, style: { type: "highlight", tint: translucentTint(PASSAGE_BLOCK_TINT), enforceContrast: false } });
                passage.push({ id: "passage", locator: exact, style: { type: "highlight", tint: translucentTint(PASSAGE_TINT), enforceContrast: false } });
            }
            drawPassage(navigator);
        },
        setAnnotations: (marks) => {
            annotationDecorations = marks.flatMap((mark) => {
                const locator = fromLocatorJson(mark.locator);
                return locator ? [{ id: mark.id, locator, style: { type: "highlight", tint: translucentTint(mark.tint), enforceContrast: false } }] : [];
            });
            // Un groupe vide efface les surlignages retirés.
            navigator.applyDecorations(annotationDecorations, ANNOTATION_GROUP);
            drawAnnotations(navigator);
        },
        clearSelection: () => {
            for (const frame of pageFrames(container)) {
                try {
                    frame.contentWindow?.getSelection()?.removeAllRanges();
                }
                catch {
                    // Page en cours de remplacement.
                }
            }
            emitted = lastPolled = "";
        },
        positionOf: (href, progression) => {
            const base = href.split("#")[0];
            let found;
            positions.forEach((p, i) => {
                if (p.href.split("#")[0] !== base)
                    return;
                if (found === undefined || (p.locations?.progression ?? 0) <= progression)
                    found = i + 1;
            });
            return found;
        },
        submitPreferences: async (preferences, isSystemDark, layout) => {
            epubPreferences = toEpubPreferences(preferences, isSystemDark, layout);
            if (fixedLayout && (preferences.spreads ?? null) !== appliedSpreads) {
                // Le nombre de pages est fixé à l'ouverture du livre : il est rouvert sur la même page.
                applySpreads(preferences.spreads ?? null);
                await recover();
                return;
            }
            // Une nouvelle mise en page (taille du texte, colonnes, largeur) ramène Readium au début
            // de la ressource : on revient à la page lue.
            const before = navigator.currentLocator;
            await navigator.submitPreferences(new EpubPreferences(epubPreferences));
            // Pendant la reprise de lecture, la protection s'en charge déjà.
            if (!restoring && (before.locations?.progression ?? 0) > 0) {
                await navigate((cb) => navigator.go(before, false, cb));
            }
        },
        destroy: () => {
            clearInterval(poll);
            return navigator.destroy();
        },
    };
    return controller;
}
//# sourceMappingURL=reader.js.map